import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import {
  Bid,
  BidBuyerProjectFee,
  BidBuyerProjectFeesCollection,
  CurrenciesCollection,
  Currency,
  Project,
  ProjectViewUser,
  QuotationFundRequest,
  QuotationFundRequestsCollection,
  QuotationRevision,
} from '@freelancer/datastore/collections';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight, TextAlign, TextSize } from '@freelancer/ui/text';
import { QuotationFundRequestStatusApi } from 'api-typings/quotations/quotations';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-quotation-award-modal',
  template: `
    <ng-container *flModalTitle i18n="Award Modal Heading">
      Award Quotation
    </ng-container>
    <fl-bit
      class="AwardPageContainer"
      *ngIf="quotationCurrency$ | flAsync as currency; else Loading"
    >
      <app-quotation-award-modal-info
        *ngIf="quotationCurrency$ | flAsync"
        [freelancer]="freelancer"
        [fundRequest]="quotationFundRequest$ | flAsync"
        [projectFee]="(projectFee$ | flAsync)?.amount"
        [quotation]="quotation"
        [currencyCode]="currency.code"
      >
      </app-quotation-award-modal-info>
      <app-quotation-award-modal-button
        [bid]="bid"
        [currencyCode]="currency.code"
        [fundRequest]="quotationFundRequest$ | flAsync"
        [project]="project"
        [quotation]="quotation"
        [flMarginBottom]="Margin.XLARGE"
      >
      </app-quotation-award-modal-button>
    </fl-bit>

    <ng-template #Loading>
      <fl-spinner
        flTrackingLabel="AwardModalInitialisationSpinner"
        [overlay]="true"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./quotation-award-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotationAwardModalComponent implements OnInit {
  FontWeight = FontWeight;
  Margin = Margin;
  TextAlign = TextAlign;
  TextSize = TextSize;

  @Input() freelancer: ProjectViewUser;
  @Input() quotation: QuotationRevision;
  @Input() bid: Bid;
  @Input() project: Project;
  @Input() showUpgrades = false;

  quotationCurrency$: Rx.Observable<Currency>;
  projectFee$: Rx.Observable<BidBuyerProjectFee>;
  quotationFundRequest$: Rx.Observable<QuotationFundRequest | undefined>;

  constructor(private datastore: Datastore) {}

  ngOnInit(): void {
    this.quotationCurrency$ = this.datastore
      .collection<CurrenciesCollection>('currencies')
      .valueChanges()
      .pipe(
        map(
          currencies =>
            currencies.find(c => c.id === this.quotation.defaultCurrencyId) ??
            currencies[0],
        ),
      );

    this.quotationFundRequest$ = this.datastore
      .collection<QuotationFundRequestsCollection>(
        'quotationFundRequests',
        query =>
          query
            .where('quotationId', '==', this.quotation.quotationId)
            .where('status', '==', QuotationFundRequestStatusApi.PENDING)
            .limit(1),
      )
      .valueChanges()
      .pipe(map(f => (f.length > 0 ? f[0] : undefined)));

    this.projectFee$ = this.datastore
      .document<BidBuyerProjectFeesCollection>(
        'bidBuyerProjectFees',
        this.bid.id,
      )
      .valueChanges();
  }
}
